import * as React from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { accountTierEnumMap } from 'configuration/specs.js';
import TextField from '@mui/material/TextField';
import {
  Button, Chip, Typography, Tooltip
} from '@material-ui/core/';


const statusRowColors = {
  paid: '#e8f5e9',
  sus: '#fff9c4',
  free: '#e0f7fa',
  new: '#f5f5f5',
};

const defaultColumns = [
    { field: 'name', headerName: 'Account Name', width: 220 },
    {
      field: 'created_at',
      headerName: 'Created on',
      width: 180,
      type: 'date',
    },
    {
      field: 'plan',
      headerName: 'Status',
      type: 'singleSelect',
      width: 140,
      valueOptions: Object.values(accountTierEnumMap),
    },
    {
      field: 'connected_services',
      headerName: 'Services',
      width: 140,
    },
    {
      field: 'connected_integrations',
      headerName: 'PMS',
      width: 140,
    },
    
];
const metricsColumns = [
    { field: 'account status', headerName: 'Account status'},
    { field: '% listings live', headerName: '% Listings live'},
    { field: 'active_plans', headerName: 'Active subscriptions'},
    { field: 'inactive features', headerName: 'Inactive features', width: 300},
    { field: 'active features', headerName: 'Active features', width: 300},
    { field: 'paired devices', headerName: 'Paired devices'},
    { field: 'unpaired devices', headerName: 'Unpaired devices'},
    { field: '# fees', headerName: '# fees'},
    { field: '# guidebooks', headerName: '# guidebooks'},
    { field: '# checkin steps', headerName: '# checkin steps'},
    { field: 'cities', headerName: 'Cities', width: 400},
    { field: 'countries', headerName: 'Countries', width: 250},
    { field: 'inactive groups', headerName: 'OFF groups'},
    { field: 'active groups', headerName: 'ON groups'},                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                     
    { field: 'inactive experiences', headerName: 'OFF Experiences'},
    { field: 'active experiences', headerName: 'ON Experiences'},
    { field: 'active listings', headerName: 'ON Listings'},
    { field: 'inactive listings', headerName: 'OFF Listings'},
    { field: 'price per listing', headerName: 'Price per listing'},
    { 
      field: 'billing date', headerName: 'Billing date',
      type: 'date',
      valueGetter: (params) => {
        // Convert the timestamp to a date object
        const timestamp = params.value;
        if (timestamp) {
          return new Date(timestamp); // You can format the date as needed
        }
        return undefined; // Return an empty string for invalid or missing timestamps
      },
    },
    { field: 'billing_issues', headerName: 'Billing issues'},
    { field: 'billing currency', headerName: 'Currency'},
    { field: 'prepayment discount value', headerName: 'Prepayment monthly discount'},
    { field: '# months prepayment discount', headerName: '# months prepaid'},
    { field: 'upcoming nso amount', headerName: 'nso total'},
    { field: 'upcoming sh amount', headerName: 'sh total'},
    { field: 'upcoming shp amount', headerName: 'shp total'},
    { field: 'upcoming ah amount', headerName: 'ah total'},
    { field: 'upcoming ahp amount', headerName: 'ahp total'},
    { field: 'upcoming ahc amount', headerName: 'ahc total'},
    { field: 'upcoming sms amount', headerName: 'sms total'},
    { field: 'upcoming whs amount', headerName: 'whs total'},
    { field: 'upcoming ai amount', headerName: 'ai total'},
    { field: 'upcoming dvc amount', headerName: 'dvc total'},
]

export default function AccountsTable({ loading, totals, accounts, createAccount, selectAccount, loadMetrics }) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [columns, setColumns] = React.useState(defaultColumns);
  const [filters, setFilters] = React.useState([]);
  const [filterApplied, setFilterApplied] = React.useState(true); // Step 1

  const accountsWithoutIds = loading ? [] : accounts.map(obj => {
    obj.id = obj.enso_key;
    obj.plan = accountTierEnumMap[obj.plan];
    obj.created_at = new Date(obj.created_at);
    return obj;
  });

  const filteredAccounts = accountsWithoutIds.filter((account) => {
    return (
      (!filterApplied || account.plan !== 'off') && // Check the "off" plan filter
      (!filterApplied || !account.name.toLowerCase().includes(' demo')) && // Check the "demo" name filter
      (account.name.toLowerCase().includes(searchTerm.toLowerCase())
       || account.enso_key.includes(searchTerm))
    );
  });


  function BillingMetric({label, totals}){
    let tooltip_text = `BREAKDOWN (Excluding Prorations):
Enso Subscription $${totals.nso}\n
Autohost: $${totals.ah}\n
Autohost plus $${totals.ahp}
Autohost check: $${totals.ahc}
EnsoAI $${totals.ai}
Devices $${totals.dvc}
Superhog $${totals.sh}
Superhog plus $${totals.shp}
SMS $${totals.sms}`
    return <Tooltip title={tooltip_text}>
      <div style={{marginLeft: 15, marginRight: 15}}>
        <Typography>${totals.total} CAD</Typography>
        {label}
      </div>
    </Tooltip>
  }

  return (
    <div style={{ height: '100%' }}>
      <div style={{ display: 'flex', marginBottom: 10, alignItems: 'center' }}>
        <div style={{ flex: 1}}>
          <TextField
            label="Search by name or enso_key"
            variant="outlined"
            value={searchTerm}
            fullWidth
            style={{maxWidth: 300}}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

        <Chip
          style={{marginLeft: 10}}
          label="Live Accounts Only"
          clickable
          variant='contained'
          onClick={() => setFilterApplied(!filterApplied)}
          variant={!!filterApplied ? "default" : "outlined"}

        />
        </div>
        {!!totals && <>
          <BillingMetric label='Charged this month' totals={totals.collected}/>
          <BillingMetric label='Upcoming this month' totals={totals.upcoming}/>
          <BillingMetric label='Suspended' totals={totals.suspended}/>
        </>}
        <Button size='small' variant='contained' onClick={e => { loadMetrics(); setColumns(defaultColumns.concat(metricsColumns)) }}>Load Metrics</Button>
        <Button size='small' variant='contained' onClick={createAccount}>Create Account</Button>
      </div>

      <DataGrid
        rows={filteredAccounts}
        loading={loading}
        columns={columns}
        onRowClick={selectAccount}
        getRowClassName={(params) => `status-${params.row.plan || 'none'}`}
        sx={{
          '& .status-paid': { backgroundColor: statusRowColors.paid },
          '& .status-sus': { backgroundColor: statusRowColors.sus },
          '& .status-free': { backgroundColor: statusRowColors.free },
          '& .status-new': { backgroundColor: statusRowColors.new },
        }}
        components={{
          Toolbar: GridToolbar,
        }}
      />
    </div>
  );
}